// TermsCell.jsx — Terms column cell with an inline "Add terms" picker.
// Shows the resolved terms label when set (click to edit); when unset, offers an
// "Add terms" affordance that opens a small popover mirroring the detail panel:
// Net 10–90 + target (supplier default | this statement only). Saving hits the same
// endpoints the panel uses, then asks the host to refresh the list.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";

const NET_OPTIONS = ["Net 10", "Net 15", "Net 20", "Net 30", "Net 45", "Net 60", "Net 90"];
const CLOSE_EVENT = "invstatement-termscell-close-others";

export default function TermsCell({ row, onSaved }) {
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();

   const hasTerms = row?.terms_label != null && row?.terms_label !== "";
   const hasDate = !!row?.sup_state_frmt_statement_date;

   const [open, setOpen] = useState(false);
   const [term, setTerm] = useState(NET_OPTIONS.includes(row?.terms_label) ? row.terms_label : "");
   const [target, setTarget] = useState("statement"); // "supplier" | "statement"
   const [saving, setSaving] = useState(false);

   const btnRef = useRef(null);
   const popRef = useRef(null);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const popId = `termscell-${row?.doc_part_id ?? "row"}`;

   const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = popRef.current?.offsetWidth || 240;
      const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - w - 8));
      setPos({ top: r.bottom + 4, left });
   };

   useEffect(() => {
      const closeOthers = (e) => { if (e.detail !== popId) setOpen(false); };
      document.addEventListener(CLOSE_EVENT, closeOthers);
      return () => document.removeEventListener(CLOSE_EVENT, closeOthers);
   }, [popId]);

   useEffect(() => {
      if (!open) return;
      const raf = requestAnimationFrame(place);
      const onChange = () => place();
      const onOutside = (e) => {
         if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
         setOpen(false);
      };
      const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("resize", onChange);
      window.addEventListener("scroll", onChange, true);
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("keydown", onEsc);
      return () => {
         cancelAnimationFrame(raf);
         window.removeEventListener("resize", onChange);
         window.removeEventListener("scroll", onChange, true);
         document.removeEventListener("mousedown", onOutside);
         document.removeEventListener("keydown", onEsc);
      };
   }, [open]);

   const toggle = (e) => {
      e.stopPropagation();
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT, { detail: popId }));
      if (!open) requestAnimationFrame(place);
      setOpen((p) => !p);
   };

   const save = async (e) => {
      e.stopPropagation();
      const netDays = parseInt(String(term).replace(/\D/g, ""), 10);
      if (Number.isNaN(netDays)) { triggerToast("warning", "Choose a term first.", false, "", false); return; }
      const isSupplier = target === "supplier";
      if (isSupplier && !row?.sm_ext_id) {
         triggerToast("warning", "Supplier id unavailable for this row yet.", false, "", false);
         return;
      }
      setSaving(true);
      try {
         const fd = new FormData();
         if (isSupplier) fd.append("_sm_ext_id", row.sm_ext_id);
         else fd.append("_ss_ext_id", row.inv_stat_id);
         fd.append("_net_days", String(netDays));
         const { responseData } = await fetchData(isSupplier ? "/updateSupplierTerms.sz" : "/updateStatementTerms.sz", "POST", fd, "form-data");
         if (responseData?.st === 1) {
            triggerToast("success", "Terms saved.", false, "", false);
            setOpen(false);
            onSaved?.();
         } else if (responseData?.st === -2 || responseData?.st === 0) {
            triggerToast("warning", "Your session has expired, please sign in.", true, "Session", false, 0);
         } else {
            triggerToast("error", responseData?.mg || "Failed to save terms.", true, "Error", false, 0);
         }
      } catch {
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      } finally {
         setSaving(false);
      }
   };

   // No statement date → terms can't be computed; mirror the panel's hint.
   if (!hasTerms && !hasDate) {
      return <span style={{ color: "#9aa0a6" }} title="Add a statement date to set terms">—</span>;
   }

   const trigger = hasTerms ? (
      <span
         className="dt-record-link"
         style={{ cursor: "pointer" }}
         onClick={toggle}
         ref={btnRef}
         title="Edit terms"
      >
         {row.terms_label}
      </span>
   ) : (
      <span
         className="dt-record-link"
         style={{ cursor: "pointer" }}
         onClick={toggle}
         ref={btnRef}
      >
         Add terms
      </span>
   );

   return (
      <span onClick={(e) => e.stopPropagation()}>
         {trigger}
         {open &&
            createPortal(
               <div
                  ref={popRef}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                     position: "fixed", top: pos.top, left: pos.left, zIndex: 3000,
                     width: 240, background: "#fff", border: "1px solid #e5e7eb",
                     borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", padding: 12,
                  }}
               >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Terms</div>
                  <select
                     className="form-select form-select-sm mb-2"
                     value={term}
                     onChange={(e) => setTerm(e.target.value)}
                  >
                     <option value="">Select terms…</option>
                     {NET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <div className="form-check" style={{ fontSize: 13 }}>
                     <input className="form-check-input" type="radio" id={`${popId}-sup`} name={`${popId}-t`}
                            checked={target === "supplier"} onChange={() => setTarget("supplier")} />
                     <label className="form-check-label" htmlFor={`${popId}-sup`}>
                        Default for {row?.sm_name || "this supplier"}
                     </label>
                  </div>
                  <div className="form-check mb-2" style={{ fontSize: 13 }}>
                     <input className="form-check-input" type="radio" id={`${popId}-stmt`} name={`${popId}-t`}
                            checked={target === "statement"} onChange={() => setTarget("statement")} />
                     <label className="form-check-label" htmlFor={`${popId}-stmt`}>
                        This statement only
                     </label>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                     <button type="button" className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); setOpen(false); }} disabled={saving}>Cancel</button>
                     <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving || !term}>{saving ? "Saving…" : "Save"}</button>
                  </div>
               </div>,
               document.body
            )}
      </span>
   );
}
